import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, Check, Loader2, ArrowRight, ArrowLeft, Building2, FileText, ShieldCheck, CheckCircle2, Clock } from "lucide-react";
import { API } from "./shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

const cfg = { withCredentials: true };
const fmt = (n) => "$" + Number(n || 0).toLocaleString();

// Fallback bracket tiers (used only until live config loads from the backend).
const FALLBACK_TIERS = [
  { tier_start: 1, tier_end: 4, rate: 4995 }, { tier_start: 5, tier_end: 9, rate: 2000 },
  { tier_start: 10, tier_end: 25, rate: 1250 }, { tier_start: 26, tier_end: 50, rate: 1000 },
  { tier_start: 51, tier_end: null, rate: 750 },
];
const FALLBACK_LICENSE_FEE = 6995;

function bracket(tiers, n) {
  const lines = []; let subtotal = 0;
  for (const t of tiers) {
    if (n < t.tier_start) break;
    const upper = t.tier_end === null || t.tier_end == null ? n : Math.min(n, t.tier_end);
    const qty = upper - t.tier_start + 1;
    if (qty <= 0) continue;
    const total = qty * t.rate;
    subtotal += total;
    lines.push({ s: t.tier_start, e: t.tier_end, r: t.rate, qty, total });
  }
  return { lines, subtotal };
}

const STEPS = ["Authorizer Details", "Pick Schools", "Review & Quote", "Vendor & PO"];

export default function SchoolOnboardingWizard({ user }) {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);
  const [details, setDetails] = useState({
    authorizer_name: "", org_name: "", email: user?.email || "", phone: "",
    ap_contact_name: "", ap_contact_email: "", ap_contact_phone: "", ap_contact_title: "",
    billing_entity_name: "", billing_address: "", purchasing_method: "PO",
    expected_timeline: "", salesperson_name: "", salesperson_email: "", sales_channel: "Direct", sales_contact_id: "",
  });
  const [selected, setSelected] = useState({}); // id -> school
  const [license, setLicense] = useState(true);
  const [quote, setQuote] = useState(null);
  const [salesContacts, setSalesContacts] = useState([]);
  const [pricing, setPricing] = useState({ tiers: FALLBACK_TIERS, portfolio_license_fee: FALLBACK_LICENSE_FEE });
  const [finishing, setFinishing] = useState(false);
  const isPilot = !!user?.is_pilot;
  const STEPS_LIST = isPilot ? ["Authorizer Details", "Pick Schools"] : STEPS;

  useEffect(() => {
    axios.get(`${API}/api/employees/sales-contacts`, cfg)
      .then((r) => setSalesContacts(r.data || []))
      .catch(() => setSalesContacts([]));
    axios.get(`${API}/api/school-onboarding/pricing`)
      .then((r) => { if (r.data?.tiers?.length) setPricing(r.data); })
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/onboarding/me`, cfg);
      setMe(r.data);
      if (r.data.details) setDetails((p) => ({ ...p, ...Object.fromEntries(
        Object.entries(r.data.details).filter(([, v]) => v !== null && v !== "")) }));
      if (r.data.quote) { setQuote(r.data.quote); setStep(3); }
    } catch { toast.error("Failed to load onboarding"); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const selectedList = useMemo(() => Object.values(selected), [selected]);
  const count = selectedList.length;
  const licenseFee = pricing.portfolio_license_fee || 0;
  const preview = useMemo(() => bracket(pricing.tiers || FALLBACK_TIERS, count), [count, pricing]);
  const annualTotal = preview.subtotal + (license ? licenseFee : 0);

  const saveDetails = async () => {
    try { await axios.put(`${API}/api/onboarding/details`, details, cfg); }
    catch { /* non-blocking */ }
  };

  // Pilot portfolios skip Review & Quote / Vendor & PO entirely: after picking
  // schools we create the portfolio + selections then provision at $0, leaving
  // the schools ready to invite from the Activation Center.
  const finishPilot = async () => {
    if (count === 0) { toast.error("Please select at least one school."); return; }
    setFinishing(true);
    try {
      await saveDetails();
      const r = await axios.post(`${API}/api/onboarding/quote`, {
        school_directory_ids: selectedList.map((s) => s.id),
        portfolio_license: false, details,
      }, cfg);
      const qid = r.data?.quote?.id;
      await axios.post(`${API}/api/pilot/portfolio/finish`, { quote_id: qid }, { withCredentials: true });
      toast.success("Portfolio ready — invite your schools");
      window.location.href = "/portfolio/activation";
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "SCHOOLS_ALREADY_ASSIGNED")
        toast.error("Some schools are already assigned to another portfolio: " + (d.schools || []).join(", "));
      else toast.error(typeof d === "string" ? d : (d?.message || "Failed to finish pilot setup"));
    } finally { setFinishing(false); }
  };

  if (loading) return <div className="flex items-center gap-2 text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;

  const noState = !me?.assigned_state;

  // Once the order is submitted/paid/activated, the wizard is locked — the portfolio
  // is managed from the Activation Center (re-running the wizard previously wiped it).
  const pf = me?.portfolio;
  const q = me?.quote;
  const finalized = pf && (
    ["Active", "Provisioned", "Pending Approval"].includes(pf.status) ||
    ["Provisioned", "PO Submitted", "PO Accepted"].includes(q?.status) ||
    ["paid", "po_submitted", "po_received"].includes(q?.payment_status));

  if (finalized) {
    const active = pf.status === "Active" || q?.status === "Provisioned" || q?.payment_status === "paid";
    const pending = !active; // PO submitted / awaiting approval
    return (
      <div className="max-w-3xl mx-auto" data-testid="onboarding-finalized">
        <div className="mb-6">
          <div className="text-[11px] font-semibold tracking-[0.14em] uppercase text-slate-400">MySchoolRx</div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">School Onboarding</h1>
        </div>
        <div className={`rounded-2xl border p-8 text-center ${active ? "border-emerald-200 bg-emerald-50" : "border-blue-200 bg-blue-50"}`}>
          {active ? <CheckCircle2 className="h-10 w-10 text-emerald-600 mx-auto" /> : <Clock className="h-10 w-10 text-blue-600 mx-auto" />}
          <h2 className="mt-3 text-xl font-bold text-slate-900">
            {active ? "Your portfolio is set up and active" : "Your order has been submitted"}
          </h2>
          <p className="mt-2 text-sm text-slate-600 max-w-lg mx-auto">
            {active
              ? "Onboarding is complete. To add, invite, or manage schools, use the Activation Center — the wizard is locked so your setup can't be overwritten."
              : "589 Labs is reviewing your Purchase Order. Once approved, your portfolio activates and you can invite schools from the Activation Center."}
          </p>
          {q && <p className="mt-2 text-xs text-slate-500" data-testid="finalized-quote-line">{isPilot ? `Founding Partner · ${q.selected_school_count} school(s) · no payment required` : `Quote ${q.quote_number} · ${fmt(q.annual_total)} · ${q.selected_school_count} school(s)`}</p>}
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            {active && (
              <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => (window.location.href = "/portfolio/activation")} data-testid="finalized-goto-activation">
                Open Activation Center <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            )}
            {!isPilot && (
              <Button variant={active ? "outline" : "default"} className={active ? "" : "bg-[#007a9e] hover:bg-[#00607d]"} onClick={() => (window.location.href = "/portfolio/vendor-po")} data-testid="finalized-goto-vendorpo">
                {active ? "View Vendor & PO" : "View order status"}
              </Button>
            )}
            <Button variant="outline" onClick={() => (window.location.href = "/portfolio")} data-testid="finalized-goto-dashboard">
              Go to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto" data-testid="onboarding-wizard">
      <div className="mb-6">
        <div className="text-[11px] font-semibold tracking-[0.14em] uppercase text-slate-400">MySchoolRx</div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">School Onboarding</h1>
        <p className="text-sm text-slate-500 mt-1">{isPilot ? "Set up your pilot portfolio and select the schools you want to invite." : "Set up your authorizer portfolio, select schools, and generate a quote."}</p>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-1">
        {STEPS_LIST.map((s, i) => (
          <React.Fragment key={s}>
            <div className={`flex items-center gap-2 whitespace-nowrap ${i === step ? "text-red-700" : i < step ? "text-slate-700" : "text-slate-400"}`}>
              <span className={`h-6 w-6 rounded-full grid place-items-center text-xs font-semibold ${i === step ? "bg-red-600 text-white" : i < step ? "bg-slate-900 text-white" : "bg-slate-200 text-slate-500"}`}>
                {i < step ? <Check className="h-3.5 w-3.5" /> : i + 1}
              </span>
              <span className="text-sm font-medium hidden sm:inline">{s}</span>
            </div>
            {i < STEPS_LIST.length - 1 && <div className="h-px w-6 sm:w-10 bg-slate-200 shrink-0" />}
          </React.Fragment>
        ))}
      </div>

      {/* STEP 1 */}
      {step === 0 && (
        <div className="space-y-6" data-testid="onboarding-step-1">
          <Section title="Authorizer" >
            <Field label="Authorizer / Portfolio Owner Name" v={details.authorizer_name} on={(v) => setDetails({ ...details, authorizer_name: v })} testid="ob-authorizer-name" />
            <Field label="Organization / Network Name" v={details.org_name} on={(v) => setDetails({ ...details, org_name: v })} testid="ob-org-name" />
            <Field label="Email" v={details.email} on={(v) => setDetails({ ...details, email: v })} testid="ob-email" />
            <Field label="Phone" v={details.phone} on={(v) => setDetails({ ...details, phone: v })} testid="ob-phone" />
          </Section>
          {!isPilot && (<>
          <Section title="AP / Business Office Contact">
            <Field label="AP Contact Name" v={details.ap_contact_name} on={(v) => setDetails({ ...details, ap_contact_name: v })} testid="ob-ap-name" />
            <Field label="AP Contact Title" v={details.ap_contact_title} on={(v) => setDetails({ ...details, ap_contact_title: v })} testid="ob-ap-title" />
            <Field label="AP Contact Email" v={details.ap_contact_email} on={(v) => setDetails({ ...details, ap_contact_email: v })} testid="ob-ap-email" />
            <Field label="AP Contact Phone" v={details.ap_contact_phone} on={(v) => setDetails({ ...details, ap_contact_phone: v })} testid="ob-ap-phone" />
          </Section>
          <Section title="Billing & Purchasing">
            <Field label="Billing Entity Name" v={details.billing_entity_name} on={(v) => setDetails({ ...details, billing_entity_name: v })} testid="ob-billing-entity" />
            <Field label="Billing Address" v={details.billing_address} on={(v) => setDetails({ ...details, billing_address: v })} testid="ob-billing-address" />
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Purchasing Method</label>
              <select data-testid="ob-purchasing-method" value={details.purchasing_method}
                onChange={(e) => setDetails({ ...details, purchasing_method: e.target.value })}
                className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white">
                {["PO", "Stripe", "Manual Invoice", "Other"].map((o) => <option key={o}>{o}</option>)}
              </select>
            </div>
            <Field label="Expected Purchase Timeline" v={details.expected_timeline} on={(v) => setDetails({ ...details, expected_timeline: v })} testid="ob-timeline" />
          </Section>
          <Section title="Sales & Attribution">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Sales Contact (589 Labs)</label>
              <select data-testid="ob-sales-contact" value={details.sales_contact_id || ""}
                onChange={(e) => {
                  const c = salesContacts.find((x) => x.id === e.target.value);
                  setDetails({ ...details, sales_contact_id: e.target.value,
                    salesperson_name: c?.name || "", salesperson_email: c?.email || "" });
                }}
                className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white">
                <option value="">Direct Sale / No Sales Contact</option>
                {salesContacts.map((c) => <option key={c.id} value={c.id}>{c.name}{c.title ? ` — ${c.title}` : ""}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Sales Channel</label>
              <select data-testid="ob-sales-channel" value={details.sales_channel}
                onChange={(e) => setDetails({ ...details, sales_channel: e.target.value })}
                className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white">
                {["Direct", "Referral", "Partner", "Inbound", "Other"].map((o) => <option key={o}>{o}</option>)}
              </select>
            </div>
          </Section>
          </>)}
          <div className="flex justify-end">
            <Button data-testid="ob-step1-next" className="bg-red-700 hover:bg-red-800"
              onClick={async () => { await saveDetails(); setStep(1); }}>
              Continue <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* STEP 2 */}
      {step === 1 && (
        noState ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-6 text-center" data-testid="onboarding-no-state">
            <ShieldCheck className="h-6 w-6 text-amber-600 mx-auto mb-2" />
            <p className="text-sm text-amber-800 font-medium">Your account needs an assigned State before school selection can begin.</p>
            <p className="text-xs text-amber-700 mt-1">Please contact 589 Labs to set your assigned State.</p>
            <Button variant="outline" className="mt-4" onClick={() => setStep(0)}>Back</Button>
          </div>
        ) : (
          <PickSchools state={me.assigned_state} selected={selected} setSelected={setSelected}
            onBack={() => setStep(0)} onNext={isPilot ? finishPilot : () => setStep(2)}
            nextLabel={isPilot ? (finishing ? "Setting up…" : "Finish setup & invite schools") : undefined}
            nextDisabled={isPilot && finishing} />
        )
      )}

      {/* STEP 3 */}
      {step === 2 && (
        <div className="space-y-6" data-testid="onboarding-step-3">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 rounded-lg border border-slate-200 bg-white p-5">
              <h3 className="font-semibold text-slate-900 mb-3">Selected schools ({count})</h3>
              <div className="max-h-72 overflow-y-auto divide-y divide-slate-100">
                {selectedList.map((s) => (
                  <div key={s.id} className="flex items-center justify-between py-2 text-sm">
                    <span className="text-slate-800">{s.school_name}</span>
                    <span className="text-slate-400">{s.city}, {s.state}</span>
                  </div>
                ))}
                {count === 0 && <p className="text-sm text-slate-400 py-6 text-center">No schools selected. Go back to Step 2.</p>}
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-5" data-testid="onboarding-pricing">
              <h3 className="font-semibold text-slate-900 mb-3">Pricing</h3>
              <div className="space-y-1.5 text-sm">
                {preview.lines.map((l, i) => (
                  <div key={i} className="flex justify-between text-slate-600">
                    <span>{l.qty} × {fmt(l.r)}</span><span>{fmt(l.total)}</span>
                  </div>
                ))}
                <div className="flex justify-between border-t border-slate-200 pt-1.5 text-slate-700">
                  <span>Subtotal</span><span className="font-medium">{fmt(preview.subtotal)}</span>
                </div>
                <label className="flex items-start gap-2 py-2 cursor-pointer">
                  <Checkbox checked={license} onCheckedChange={(v) => setLicense(!!v)} data-testid="ob-license-toggle" className="mt-0.5" />
                  <span className="text-xs text-slate-600">
                    <span className="font-medium text-slate-800">Portfolio Manager License</span> — {fmt(licenseFee)}/yr.
                    Unlocks cross-school dashboards, renewal readiness, benchmark analysis, and portfolio risk reporting.
                  </span>
                </label>
                <div className="flex justify-between border-t border-slate-900/10 pt-2 text-base font-bold text-slate-900">
                  <span>Estimated annual total</span><span data-testid="ob-annual-total">{fmt(annualTotal)}</span>
                </div>
                <p className="text-[11px] text-slate-400 pt-1">State and local taxes are not included. Applicable taxes, if any, are calculated at invoice time.</p>
              </div>
            </div>
          </div>
          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(1)}><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
            <Button data-testid="ob-create-quote" className="bg-red-700 hover:bg-red-800" disabled={count === 0}
              onClick={async () => {
                try {
                  const r = await axios.post(`${API}/api/onboarding/quote`, {
                    school_directory_ids: selectedList.map((s) => s.id),
                    portfolio_license: license, details,
                  }, cfg);
                  setQuote(r.data.quote); setStep(3); toast.success("Quote created");
                } catch (e) {
                  const d = e?.response?.data?.detail;
                  if (d?.code === "PORTFOLIO_FINALIZED") {
                    toast.error(d.message || "Your order is already submitted.");
                    load();
                  } else if (d?.code === "SCHOOLS_ALREADY_ASSIGNED")
                    toast.error("Some schools are already assigned to another portfolio: " + (d.schools || []).join(", "));
                  else toast.error(typeof d === "string" ? d : "Failed to create quote");
                }
              }}>
              Create Quote <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* STEP 4 */}
      {step === 3 && quote && (
        <div className="space-y-6" data-testid="onboarding-step-4">
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-5 flex items-start gap-3">
            <div className="h-9 w-9 rounded-full bg-emerald-600 grid place-items-center shrink-0"><Check className="h-5 w-5 text-white" /></div>
            <div>
              <h3 className="font-semibold text-emerald-900">Your quote has been created</h3>
              <p className="text-sm text-emerald-800">The next step is to send the vendor package to your AP/business office contact and begin the PO process.</p>
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid="onboarding-quote-summary">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
              <div>
                <div className="text-xs uppercase tracking-wide text-slate-400">Quote</div>
                <div className="text-lg font-bold text-slate-900">{quote.quote_number}</div>
              </div>
              <div className="text-right">
                <div className="text-xs uppercase tracking-wide text-slate-400">Annual Total</div>
                <div className="text-lg font-bold text-slate-900">{fmt(quote.annual_total)}</div>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <Meta label="Schools" v={quote.selected_school_count} />
              <Meta label="Subtotal" v={fmt(quote.subtotal)} />
              <Meta label="Portfolio Manager License" v={quote.portfolio_license_included ? fmt(quote.portfolio_license_fee) : "Not included"} />
              <Meta label="Status" v={quote.status} />
              <Meta label="Product" v={quote.product_name} />
              <Meta label="Payment Method" v={quote.payment_method} />
              <Meta label="Expires" v={quote.quote_expiration_date} />
              <Meta label="Org" v={quote.authorizer_org_name} />
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-5 text-sm text-slate-600">
            <div className="flex items-center gap-2 font-medium text-slate-800 mb-1"><FileText className="h-4 w-4" /> Next steps</div>
            Use this quote to create your organization's purchase order. Once your PO is issued, upload it in
            <a href="/portfolio/vendor-po" className="text-red-700 font-medium"> Vendor &amp; PO</a> or share it through the secure AP link.
          </div>
          <div className="flex gap-3">
            <Button className="bg-slate-900 hover:bg-black" onClick={() => (window.location.href = "/portfolio/vendor-po")} data-testid="ob-goto-vendor">
              Go to Vendor &amp; PO <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
            <Button variant="outline" onClick={() => (window.location.href = "/portfolio/dashboard")}>View Portfolio Dashboard</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-5">
      <h3 className="font-semibold text-slate-900 mb-4">{title}</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>
    </div>
  );
}
function Field({ label, v, on, testid }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-500 mb-1">{label}</label>
      <Input data-testid={testid} value={v || ""} onChange={(e) => on(e.target.value)} />
    </div>
  );
}
function Meta({ label, v }) {
  return (
    <div><div className="text-xs uppercase tracking-wide text-slate-400">{label}</div>
      <div className="text-slate-800 font-medium truncate">{v}</div></div>
  );
}

function PickSchools({ state, selected, setSelected, onBack, onNext, nextLabel, nextDisabled }) {
  const [q, setQ] = useState("");
  const [data, setData] = useState({ schools: [], total: 0, page: 1, pages: 1 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/onboarding/schools`, { ...cfg, params: { q, page: p, page_size: 24 } });
      setData(r.data); setPage(r.data.page);
    } catch (e) {
      if (e?.response?.data?.detail === "NO_ASSIGNED_STATE") toast.error("No assigned state");
      else toast.error("Failed to load schools");
    }
    setLoading(false);
  }, [q]);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => load(1), 300);
    return () => clearTimeout(timer.current);
  }, [q, load]);

  const toggle = (s) => setSelected((prev) => {
    const n = { ...prev };
    if (n[s.id]) delete n[s.id]; else n[s.id] = s;
    return n;
  });
  const count = Object.keys(selected).length;

  return (
    <div data-testid="onboarding-step-2">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="text-sm text-slate-600">
          Selecting schools in <span className="font-semibold text-slate-900">{state}</span> · {data.total} available
        </div>
        <div className="text-sm font-medium text-red-700" data-testid="ob-selected-count">{count} selected</div>
      </div>
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input data-testid="ob-school-search" className="pl-9" placeholder="Search school, city, ZIP or NCES ID…"
          value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 min-h-[200px]">
        {data.schools.map((s) => {
          const isSel = !!selected[s.id];
          const unavailable = !s.available;
          return (
            <button key={s.id} disabled={unavailable} onClick={() => toggle(s)}
              data-testid={`ob-school-${s.id}`}
              className={`text-left rounded-lg border p-3 transition-colors ${
                unavailable ? "border-slate-100 bg-slate-50 opacity-70 cursor-not-allowed"
                  : isSel ? "border-red-500 bg-red-50 ring-1 ring-red-200" : "border-slate-200 bg-white hover:border-slate-300"}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-medium text-slate-900 text-sm truncate flex items-center gap-1">
                    <Building2 className="h-3.5 w-3.5 text-slate-400 shrink-0" /> {s.school_name}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5 truncate">{s.city}, {s.state} · {s.county || "—"}</div>
                  <div className="text-[11px] text-slate-400 mt-0.5">{s.school_type || "—"} · NCES {s.nces_school_id || "—"}</div>
                </div>
                {unavailable
                  ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-500 shrink-0">Assigned</span>
                  : isSel && <span className="h-5 w-5 rounded-full bg-red-600 grid place-items-center shrink-0"><Check className="h-3 w-3 text-white" /></span>}
              </div>
              {unavailable && <div className="text-[10px] text-slate-400 mt-1">Already assigned to another Authorizer Portfolio</div>}
            </button>
          );
        })}
        {loading && <div className="col-span-full text-center text-slate-400 py-6"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>}
        {!loading && data.schools.length === 0 && <div className="col-span-full text-center text-slate-400 py-10">No schools found.</div>}
      </div>
      <div className="flex items-center justify-between mt-4">
        <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => load(page - 1)} data-testid="ob-prev">Previous</Button>
        <span className="text-xs text-slate-500">Page {data.page} of {data.pages}</span>
        <Button variant="outline" size="sm" disabled={page >= data.pages} onClick={() => load(page + 1)} data-testid="ob-next">Next</Button>
      </div>
      <div className="flex justify-between mt-6">
        <Button variant="outline" onClick={onBack}><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
        <Button className="bg-red-700 hover:bg-red-800" disabled={count === 0 || nextDisabled} onClick={onNext} data-testid="ob-step2-next">
          {nextLabel || "Review Pricing"} <ArrowRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}
